// import { Document, Page, View } from '@react-pdf/renderer';
// import styles from '../../styles';
// import Header from './Header';
// import { TableHeader, TableDetails } from './TableSection';
// import TotalSection from './TotalSection';
// import Footer from './Footer';

// /**
//  * InvoiceDocument is a pure presentational component that
//  * receives a fully fetched `invoice` object via props.
// */

// const PurInvoiceDocument = ({ invoice, companyData }) => (
//     <Document>
//         <Page size="A4" style={styles.page} wrap>   
//             {/* Header with company and invoice meta */}
//             <Header invoice={invoice} companyData={companyData} />

//             {/* Items table */}
//             <View style={styles.tableContainer}>
//                 <TableHeader />
//                 <TableDetails items={invoice.items || []} />
//             </View>

//             {/* Totals and tax section */}
//             <TotalSection items={invoice.items || []} invoice={invoice}  />

//             {/* Footer with terms & conditions, signatory, etc. */}
//             <Footer invoice={invoice} companyData={companyData} />
//         </Page>
//     </Document>
// );

// export default PurInvoiceDocument;






import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";

const formatINR = (val) =>
    Number(val || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const normalizeDate = (v) =>
    typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)
        ? v.split("-").reverse().join("/")
        : v || "-";

const numberToWords = (n) => {
    const ones = [
        "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
        "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen",
    ];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const below20 = (num) => ones[num];
    const below100 = (num) => {
        if (num < 20) return below20(num);
        return tens[Math.floor(num / 10)] + (num % 10 ? ` ${below20(num % 10)}` : "");
    };
    const below1000 = (num) => {
        if (num < 100) return below100(num);
        return `${below20(Math.floor(num / 100))} Hundred${num % 100 ? ` ${below100(num % 100)}` : ""}`;
    };

    if (!Number.isFinite(n) || n < 0) return "";
    if (n < 1000) return below1000(n);

    const crore = 10000000,
        lakh = 100000,
        thousand = 1000;
    if (n >= crore) {
        const c = Math.floor(n / crore),
            rem = n % crore;
        return `${numberToWords(c)} Crore${rem ? ` ${numberToWords(rem)}` : ""}`;
    }
    if (n >= lakh) {
        const l = Math.floor(n / lakh),
            rem = n % lakh;
        return `${numberToWords(l)} Lakh${rem ? ` ${numberToWords(rem)}` : ""}`;
    }
    if (n >= thousand) {
        const t = Math.floor(n / thousand),
            rem = n % thousand;
        return `${numberToWords(t)} Thousand${rem ? ` ${numberToWords(rem)}` : ""}`;
    }
    return below1000(n);
};

const BoldLabel = ({ children }) => (
    <Text style={{ fontFamily: "Helvetica-Bold" }}>{children}</Text>
);

const estimateLines = (text, maxWidth, fontSize) => {
    if (!text || typeof text !== "string") {
        console.log(`estimateLines: Empty or invalid text, returning 0 lines`);
        return 0;
    }
    text = text.replace(/\s+/g, " ").trim();
    if (!text) {
        console.log(`estimateLines: Text is empty after trimming, returning 0 lines`);
        return 0;
    }
    const avgCharWidth = fontSize * 0.5;
    const charsPerLine = Math.floor(maxWidth / avgCharWidth);
    const numChars = text.length;
    const lines = numChars > 0 ? Math.ceil(numChars / charsPerLine) : 1;
    console.log(`estimateLines: Text="${text.slice(0, 20)}${text.length > 20 ? "..." : ""}", maxWidth=${maxWidth}, fontSize=${fontSize}, Chars=${numChars}, CharsPerLine=${charsPerLine}, Lines=${lines}`);
    return lines;
};

const getEstimatedLines = (text, maxWidth, fontSize) => {
    if (!text || typeof text !== "string") {
        console.log(`getEstimatedLines: Empty or invalid text, returning 0 lines`);
        return 0;
    }
    const lines = text.split("\n").reduce((sum, seg) => {
        const trimmed = seg.replace(/\s+/g, " ").trim();
        if (trimmed === "") {
            console.log(`getEstimatedLines: Empty line, adding 1 line`);
            return sum + 1;
        }
        return sum + estimateLines(trimmed, maxWidth, fontSize);
    }, 0);
    console.log(`getEstimatedLines: Total Lines=${lines}`);
    return lines;
};

const InvoiceDocument = ({ invoice, loggedInUser, companyData }) => {
    const PurInvoiceDetails = invoice;
    const company = Array.isArray(companyData) ? companyData[0] : companyData;
    const items = PurInvoiceDetails.items || [];
    const totalAmountInWords = `${numberToWords(Math.floor(PurInvoiceDetails.BillAmt || 0))} Only`;
    const isIgst = PurInvoiceDetails.IGSTAmt > 0;

    const styles = StyleSheet.create({
        page: { flexDirection: "column", padding: 25, paddingBottom: 30, fontSize: 12, fontFamily: "Helvetica", width: 794, height: 1123 },
        companyWrapper: { marginBottom: 0, flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%" },
        logo: { width: 100, height: 100, objectFit: "contain" },
        companyDetails: { fontSize: 14, maxWidth: 450, textAlign: "right", alignItems: "flex-end", padding: 5 },
        companyName: { fontFamily: "Helvetica-Bold", marginRight: -5 },
        companyAddress: { margin: 0 },
        companyPhone: { margin: 0, marginRight: -5 },
        content: { borderWidth: 1, borderColor: "black", flexGrow: 1, flexDirection: "column" },
        gstHeader: { fontSize: 14, flexDirection: "row", justifyContent: "space-between", borderBottomWidth: 1, borderColor: "black", padding: 5 },
        addressDetails: { flexDirection: "row", padding: 5, borderBottomWidth: 1, borderColor: "black" },
        leftAddress: { width: "45%", paddingRight: 5, fontSize: 13 },
        middleAddress: { width: "30%", paddingHorizontal: 25, fontSize: 13 },
        rightAddress: { width: "20%", paddingLeft: 40, fontSize: 13 },
        greeting: { fontSize: 13, padding: 5 },
        tableHeader: { flexDirection: "row", backgroundColor: "#f2f2f2", borderTopWidth: 1, borderBottomWidth: 1, borderColor: "black", textAlign: "center", fontFamily: "Helvetica-Bold" },
        tableRow: { flexDirection: "row", borderBottomWidth: 1, borderColor: "black" },
        tableTotalRow: { flexDirection: "row", fontFamily: "Helvetica-Bold" },
        colSr: { width: "4%", borderRightWidth: 1, borderColor: "black", padding: 5, textAlign: "center" },
        colDesc: { width: "37%", borderRightWidth: 1, borderColor: "black", padding: 5 },
        colHsn: { width: "10%", borderRightWidth: 1, borderColor: "black", padding: 5, textAlign: "center" },
        colQty: { width: "6%", borderRightWidth: 1, borderColor: "black", padding: 5, textAlign: "right" },
        colUnit: { width: "6%", borderRightWidth: 1, borderColor: "black", padding: 5, textAlign: "center" },
        colRate: { width: "12%", borderRightWidth: 1, borderColor: "black", padding: 5, textAlign: "right" },
        colDisc: { width: "6%", borderRightWidth: 1, borderColor: "black", padding: 5, textAlign: "right" },
        colGst: { width: "6%", borderRightWidth: 1, borderColor: "black", padding: 5, textAlign: "right" },
        colAmount: { width: "13%", borderColor: "black", padding: 5, textAlign: "right" },
        footer: { borderTopWidth: 1, borderColor: "black" },
        totalBillFooter: { fontSize: 14, textAlign: "right", padding: 3, borderTopWidth: 1, borderColor: "black" },
        totalRow: { flexDirection: "row", justifyContent: "flex-end", width: "100%", marginBottom: 2 },
        label: { width: 150, textAlign: "left" },
        colon: { width: 10, textAlign: "center" },
        value: { width: 150, textAlign: "right" },
        amountInWords: { fontSize: 14, padding: 3, borderTopWidth: 1, borderColor: "black" },
        paymentTerms: { fontSize: 14, padding: 3, borderTopWidth: 1, borderColor: "black" },
        yoursFaithfully: { fontSize: 14, padding: 5, borderTopWidth: 1, borderColor: "black" },
        terms: { flexDirection: "row", borderTopWidth: 1, borderBottomWidth: 1, borderColor: "black" },
        termsLeft: { width: "50%", fontSize: 10, padding: 5 },
        termsRight: { width: "50%", fontSize: 12, padding: 5, textAlign: "right" },
        pageNumber: { position: "absolute", fontSize: 12, bottom: 10, right: 25 },
        topSection: { flexDirection: "row", borderBottomWidth: 1, borderColor: "black" },
        topHalf: { width: "48%", padding: 5 },
        rightHalf: { borderLeftWidth: 1, borderColor: "black", paddingLeft: 10 },
        topColumns: { flexDirection: "row" },
        topCol: { width: "50%" },
        bottomSection: { flexDirection: "row", borderBottomWidth: 1, borderColor: "black" },
        bottomHalf: { width: "48%", padding: 5 },
        rightHalfBottom: { borderLeftWidth: 1, borderColor: "black", paddingLeft: 10 },
    });

    const LINE_HEIGHT_FACTOR = 1.2;
    const getLineHeight = (fontSize) => fontSize * LINE_HEIGHT_FACTOR;
    const getTextHeight = (fontSize, numLines, paddingTop, paddingBottom, borderTop = 0, borderBottom = 0) => {
        const height = getLineHeight(fontSize) * numLines + paddingTop + paddingBottom + borderTop + borderBottom;
        console.log(`getTextHeight: fontSize=${fontSize}, numLines=${numLines}, paddingTop=${paddingTop}, paddingBottom=${paddingBottom}, borderTop=${borderTop}, borderBottom=${borderBottom}, Height=${height}`);
        return height;
    };

    const CONTENT_WIDTH = 794 - 50;
    const PAGE_HEIGHT = 1073;
    const PAGE_CONTENT_HEIGHT = PAGE_HEIGHT - 50 - 30;
    const COMPANY_WRAPPER_MARGIN_BOTTOM = 5;
    const PARENT_BORDER_TOP_BOTTOM = 2;
    const FIXED_TABLE_FOOTER_SPACE = -150;

    const CompanyDetails = () => {
        // 1. Prefer the logo fetched by quotationData (quota.imageBase64)
        // 2. Fallback to companyData if you ever store it there
        let src = invoice.imageBase64 || company?.imageBase64 || '';

        // ---- NORMALISE ----
        if (src && !src.startsWith('data:image')) {
            // Assume JPEG – change to png if your API returns PNG
            src = `data:image/jpeg;base64,${src.trim()}`;
        }
        // -------------------

        return (
            <View style={styles.companyWrapper}>
                {src ? (
                    <Image style={styles.logo} src={src} />
                ) : (
                    <View style={[styles.logo, { backgroundColor: '#f0f0f0', justifyContent: 'center', alignItems: 'center' }]}>
                        <Text style={{ fontSize: 24, fontWeight: 'bold' }}>LOGO</Text>
                    </View>
                )}
                <View style={styles.companyDetails}>
                    <Text style={styles.companyName}>{company.CompanyName}</Text>
                    <Text style={styles.companyAddress}>{company.Address}</Text>
                    <Text style={styles.companyPhone}><BoldLabel>Phone:</BoldLabel> {company.MobileNo}</Text>
                </View>
            </View>
        );
    };

    const getCompanyDetailsHeight = () => {


        const fontSize = 14;
        const maxWidth = 450;
        const nameText = company.CompanyName || "";
        const addressText = company.Address || "";
        const phoneText = `Phone: ${company.MobileNo || ""}`;
        const nameLines = estimateLines(nameText, maxWidth, fontSize);
        const addressLines = estimateLines(addressText, maxWidth, fontSize);
        const phoneLines = estimateLines(phoneText, maxWidth, fontSize);
        const textHeight = getTextHeight(fontSize, nameLines + addressLines + phoneLines, 0, 0);
        const imageHeight = 100;
        const height = Math.max(textHeight, imageHeight) + COMPANY_WRAPPER_MARGIN_BOTTOM;
        console.log(`CompanyDetails Height: ${height}, Name Lines: ${nameLines}, Address Lines: ${addressLines}, Phone Lines: ${phoneLines}`);
        return height;
    };

    const GSTDetailsHeader = () => (
        <View style={styles.gstHeader}>
            <Text><BoldLabel>GSTIN:</BoldLabel> {company.GSTNo}</Text>
            <Text><BoldLabel>TAX INVOICE</BoldLabel></Text>
            <Text><BoldLabel>Original for Recipient</BoldLabel></Text>
        </View>
    );

    const getGSTDetailsHeaderHeight = () => {
        const fontSize = 14;
        const maxWidth = (CONTENT_WIDTH - 10) / 3;
        const texts = [
            `GSTIN: ${company.GSTNo || ""}`,
            "QUOTATION",
            "Original for Recipient",
        ];
        const lines = texts.reduce((sum, text) => sum + estimateLines(text, maxWidth, fontSize), 0);
        const height = getTextHeight(fontSize, lines, 5, 5, 0, 1);
        console.log(`GSTDetailsHeader Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const topLeftCol1 = {
        invoiceNo: PurInvoiceDetails.invoiceNo || "",
        poNo: PurInvoiceDetails.poNo || "",
        state: PurInvoiceDetails.clientState || ""
    };
    const topLeftCol2 = {
        stateCode: PurInvoiceDetails.clientStateCode || ""
    };
    const topRightCol1 = {
        transport: PurInvoiceDetails.transport || "SELF",
        lrNo: PurInvoiceDetails.LrNo || "",
        vehNo: PurInvoiceDetails.VehicleNo || ""
    };
    const topRightCol2 = {
        dateOfSupply: PurInvoiceDetails.supplyDate || "",
        placeOfSupply: PurInvoiceDetails.placeOfSupply || ""
    };

    const bottomLeft = {
        billedTo: PurInvoiceDetails.companyName || "",
        clientAddress: PurInvoiceDetails.clientAddress || "",
        poNo: PurInvoiceDetails.poNo || "",
        state: PurInvoiceDetails.clientState || "",
        stateCode: PurInvoiceDetails.clientStateCode || ""
    };
    const bottomRight = {
        shipTo: PurInvoiceDetails.shipToConsigneename || "",
        address: PurInvoiceDetails.address || "",
        gstin: PurInvoiceDetails.shipToGstNumber || "",
        state: PurInvoiceDetails.shipToState || "",
        stateCode: PurInvoiceDetails.shipToStateCode || ""
    };

    // ------------------- NEW TOP SECTION -------------------
    const TopSection = () => (
        <View style={styles.topSection}>
            {/* LEFT HALF */}
            <View style={styles.topHalf}>
                <View style={styles.topColumns}>
                    <View style={styles.topCol}>
                        <Text><BoldLabel>Invoice No:</BoldLabel> {topLeftCol1.invoiceNo}</Text>
                        <Text><BoldLabel>PO No:</BoldLabel> {topLeftCol1.poNo}</Text>
                        <Text><BoldLabel>State:</BoldLabel> {topLeftCol1.state}</Text>
                    </View>
                    <View style={styles.topCol}>
                        <Text><BoldLabel>Date:</BoldLabel> {normalizeDate(PurInvoiceDetails.date)}</Text>
                        <Text><BoldLabel>State Code:</BoldLabel> {topLeftCol2.stateCode}</Text>
                    </View>
                </View>
            </View>

            {/* RIGHT HALF */}
            <View style={[styles.topHalf, styles.rightHalf]}>
                <View style={styles.topColumns}>
                    <View style={styles.topCol}>
                        <Text><BoldLabel>Transport:</BoldLabel> {topRightCol1.transport}</Text>
                        <Text><BoldLabel>L.R No:</BoldLabel> {topRightCol1.lrNo}</Text>
                        <Text><BoldLabel>Veh. No:</BoldLabel> {topRightCol1.vehNo}</Text>
                    </View>
                    <View style={styles.topCol}>
                        <Text><BoldLabel>Date of Supply:</BoldLabel> {normalizeDate(topRightCol2.dateOfSupply)}</Text>
                        <Text><BoldLabel>Place of Supply:</BoldLabel> {topRightCol2.placeOfSupply}</Text>
                    </View>
                </View>
            </View>
        </View>
    );

    // ------------------- NEW BOTTOM SECTION -------------------
    const BottomSection = () => (
        <View style={styles.bottomSection}>
            {/* LEFT HALF */}
            <View style={styles.bottomHalf}>
                <Text><BoldLabel>Billed To:</BoldLabel> {bottomLeft.billedTo}</Text>
                <Text><BoldLabel>Address:</BoldLabel> {bottomLeft.clientAddress}</Text>
                <Text><BoldLabel>Your PO No:</BoldLabel> {bottomLeft.poNo}</Text>
                <Text><BoldLabel>State:</BoldLabel> {bottomLeft.state}</Text>
                <Text><BoldLabel>State Code:</BoldLabel> {bottomLeft.stateCode}</Text>
            </View>

            {/* RIGHT HALF */}
            <View style={[styles.bottomHalf, styles.rightHalfBottom]}>
                <Text><BoldLabel>Ship To:</BoldLabel> {bottomRight.shipTo}</Text>
                <Text><BoldLabel>Address:</BoldLabel> {bottomRight.address}</Text>
                <Text><BoldLabel>GSTIN No:</BoldLabel> {bottomRight.gstin}</Text>
                <Text><BoldLabel>State:</BoldLabel> {bottomRight.state}</Text>
                <Text><BoldLabel>State Code:</BoldLabel> {bottomRight.stateCode}</Text>
            </View>
        </View>
    );

    // ---- NEW TOP SECTION HEIGHT ----
    const getTopSectionHeight = () => {
        const fontSize = 13;
        const lineHeight = getLineHeight(fontSize);
        const marginBetween = 2;
        const colWidth = CONTENT_WIDTH * 0.48 - 15;   // inner width of each half

        const leftPairs = [
            `Invoice No: ${topLeftCol1.invoiceNo}`,
            `PO No: ${topLeftCol1.poNo}`,
            `State: ${topLeftCol1.state}`,
            `Date: ${normalizeDate(PurInvoiceDetails.date)}`,
            `State Code: ${topLeftCol2.stateCode}`
        ];
        const rightPairs = [
            `Transport: ${topRightCol1.transport}`,
            `L.R No: ${topRightCol1.lrNo}`,
            `Veh. No: ${topRightCol1.vehNo}`,
            `Date Of Supply: ${normalizeDate(topRightCol2.dateOfSupply)}`,
            `Place of supply: ${topRightCol2.placeOfSupply}`
        ];

        const calc = (pairs) => pairs.reduce((h, txt) => h + estimateLines(txt, colWidth, fontSize) * lineHeight + marginBetween, 0);
        const height = Math.max(calc(leftPairs), calc(rightPairs)) + 5 * 2 + 1; // padding + border-bottom
        console.log(`TopSection Height: ${height}`);
        return height;
    };

    // ---- NEW BOTTOM SECTION HEIGHT ----
    const getBottomSectionHeight = () => {
        const fontSize = 13;
        const lineHeight = getLineHeight(fontSize);
        const marginBetween = 2;
        const colWidth = CONTENT_WIDTH * 0.48 - 15;

        const leftPairs = [
            `Billed To: ${bottomLeft.billedTo}`,
            `Address: ${bottomLeft.clientAddress}`,
            `Your P.O No: ${bottomLeft.poNo}`,
            `State: ${bottomLeft.state}`,
            `State Code: ${bottomLeft.stateCode}`
        ];
        const rightPairs = [
            `Ship To: ${bottomRight.shipTo}`,
            `Address: ${bottomRight.address}`,
            `GSTIN No: ${bottomRight.gstin}`,
            `State: ${bottomRight.state}`,
            `State Code: ${bottomRight.stateCode}`
        ];

        const calc = (pairs) => pairs.reduce((h, txt) => h + estimateLines(txt, colWidth, fontSize) * lineHeight + marginBetween, 0);
        const height = Math.max(calc(leftPairs), calc(rightPairs)) + 5 * 2 + 1; // padding + border-top
        console.log(`BottomSection Height: ${height}`);
        return height;
    };

    const GreetingHeader = () => (
        <View style={styles.greeting}>
            <Text>Dear Sir,</Text>
            <Text>We thank you very much for your inquiry as noted and pleased to submit our most competitive offer for the same as under:</Text>
        </View>
    );

    const getGreetingHeaderHeight = () => {
        const fontSize = 13;
        const maxWidth = CONTENT_WIDTH - 20;
        const text = "Dear Sir,\nWe thank you very much for your inquiry as noted and pleased to submit our most competitive offer for the same as under:";
        const lines = getEstimatedLines(text, maxWidth, fontSize);
        const height = getTextHeight(fontSize, lines, 10, 10);
        console.log(`GreetingHeader Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const MainContent = ({ items, startSr }) => (
        <View style={{ flexGrow: 1 }}>
            <View style={styles.tableHeader}>
                <Text style={styles.colSr}>Sr. No.</Text>
                <Text style={styles.colDesc}>Description</Text>
                <Text style={styles.colHsn}>HSN Code</Text>
                <Text style={styles.colQty}>Qty</Text>
                <Text style={styles.colUnit}>Unit</Text>
                <Text style={styles.colRate}>Rate</Text>
                <Text style={styles.colDisc}>Disc (%)</Text>
                <Text style={styles.colGst}>Tax (%)</Text>
                <Text style={styles.colAmount}>Amount (Rs.)</Text>
            </View>
            {items.map((item, idx) => (
                <View style={styles.tableRow} key={idx}>
                    <Text style={styles.colSr}>{startSr + idx}</Text>
                    <Text style={styles.colDesc}>{item.description}</Text>
                    <Text style={styles.colHsn}>{item.hsnCode}</Text>
                    <Text style={styles.colQty}>{formatINR(item.qty)}</Text>
                    <Text style={styles.colUnit}>{item.Unit}</Text>
                    <Text style={styles.colRate}>{formatINR(item.rate)}</Text>
                    <Text style={styles.colDisc}>{formatINR(item.discount)}</Text>
                    <Text style={styles.colGst}>
                        {Number(item.igst || 0) > 0
                            ? formatINR(item.igst)
                            : formatINR((Number(item.cgst) || 0) + (Number(item.sgst) || 0))
                        }
                    </Text>
                    <Text style={styles.colAmount}>{formatINR(item.qty * item.rate * (1 - (item.discount || 0) / 100))}</Text>
                </View>
            ))}
            <View style={styles.tableTotalRow}>
                <Text style={{ width: "41%" }} />
                <Text style={{ width: "8%", textAlign: "right" }}>Total Qty:</Text>
                <Text style={{ width: "6%", textAlign: "right" }}>
                    {formatINR(items.reduce((sum, i) => sum + Number(i.qty || 0), 0))}
                </Text>
                <Text style={{ width: "45%" }} />
            </View>
        </View>
    );

    const getTableHeadHeight = () => {
        const fontSize = 12;
        const maxWidth = CONTENT_WIDTH * 0.30;
        const headers = isIgst
            ? ["Sr. No.", "Description", "HSN Code", "Qty", "Unit", "Rate", "Disc (%)", "IGST (%)", "Amount (₹)"]
            : ["Sr. No.", "Description", "HSN Code", "Qty", "Unit", "Rate", "Disc (%)", "CGST (%)", "SGST (%)", "Amount (₹)"];
        const lines = headers.reduce((sum, header) => Math.max(sum, estimateLines(header, maxWidth, fontSize)), 1);
        const height = getTextHeight(fontSize, lines, 5, 5, 1, 1);
        console.log(`TableHead Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const getRowHeight = (item) => {
        const fontSize = 12;
        const colWidths = {
            sr: CONTENT_WIDTH * 0.03,
            desc: CONTENT_WIDTH * 0.30,
            hsn: CONTENT_WIDTH * 0.08,
            qty: CONTENT_WIDTH * 0.06,
            unit: CONTENT_WIDTH * 0.04,
            rate: CONTENT_WIDTH * 0.10,
            disc: CONTENT_WIDTH * 0.05,
            gst: CONTENT_WIDTH * 0.05,
            amount: CONTENT_WIDTH * 0.12,
        };
        const descText = item.description || "";
        const hsnText = item.hsnCode || "";
        const qtyText = formatINR(item.qty || 0);
        const unitText = item.Unit || "Unit";
        const rateText = formatINR(item.rate || 0);
        const discText = formatINR(item.discount || 0);
        const amountText = formatINR(item.qty * item.rate * (1 - (item.discount || 0) / 100));

        const linesDesc = estimateLines(descText, colWidths.desc, fontSize);
        const linesHsn = estimateLines(hsnText, colWidths.hsn, fontSize);
        const linesQty = estimateLines(qtyText, colWidths.qty, fontSize);
        const linesUnit = estimateLines(unitText, colWidths.unit, fontSize);
        const linesRate = estimateLines(rateText, colWidths.rate, fontSize);
        const linesDisc = estimateLines(discText, colWidths.disc, fontSize);
        const linesAmount = estimateLines(amountText, colWidths.amount, fontSize);

        let maxLines = Math.max(linesDesc, linesHsn, linesQty, linesUnit, linesRate, linesDisc, linesAmount, 1);

        if (isIgst) {
            const igstText = Number(item.igst || 0) === 0 ? '' : formatINR(item.igst || 0);
            const linesIgst = estimateLines(igstText, colWidths.gst, fontSize);
            maxLines = Math.max(maxLines, linesIgst);
        } else {
            const cgstText = Number(item.cgst || 0) === 0 ? '' : formatINR(item.cgst || 0);
            const sgstText = Number(item.sgst || 0) === 0 ? '' : formatINR(item.sgst || 0);
            const linesCgst = estimateLines(cgstText, colWidths.gst, fontSize);
            const linesSgst = estimateLines(sgstText, colWidths.gst, fontSize);
            maxLines = Math.max(maxLines, linesCgst, linesSgst);
        }

        const height = getTextHeight(fontSize, maxLines, 5, 5, 0, 1);
        console.log(`Row Height for "${descText.slice(0, 20)}${descText.length > 20 ? "..." : ""}": ${height}, Max Lines: ${maxLines}`);
        return height;
    };

    const getTableFooterHeight = () => {
        const fontSize = 12;
        const maxWidth = CONTENT_WIDTH * 0.06;
        const totalQty = formatINR(items.reduce((sum, i) => sum + Number(i.qty || 0), 0));
        const lines = estimateLines(`Total Qty: ${totalQty}`, maxWidth, fontSize);
        const height = getTextHeight(fontSize, lines, 5, 5, 0, 0);
        console.log(`TableFooter Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const TotalBillFooter = () => (
        <View style={styles.totalBillFooter}>
            <View style={styles.totalRow}>
                <Text style={styles.label}><BoldLabel>Amount Before Tax</BoldLabel></Text>
                <Text style={styles.colon}>:</Text>
                <Text style={styles.value}>Rs. {formatINR(PurInvoiceDetails.basicAmount)}</Text>
            </View>
            {isIgst ? (
                <View style={styles.totalRow}>
                    <Text style={styles.label}><BoldLabel>IGST Amount</BoldLabel></Text>
                    <Text style={styles.colon}>:</Text>
                    <Text style={styles.value}>Rs. {formatINR(PurInvoiceDetails.IGSTAmt)}</Text>
                </View>
            ) : (
                <>
                    <View style={styles.totalRow}>
                        <Text style={styles.label}><BoldLabel>CGST Amount</BoldLabel></Text>
                        <Text style={styles.colon}>:</Text>
                        <Text style={styles.value}>Rs. {formatINR(PurInvoiceDetails.CGSTAmt)}</Text>
                    </View>
                    <View style={styles.totalRow}>
                        <Text style={styles.label}><BoldLabel>SGST Amount</BoldLabel></Text>
                        <Text style={styles.colon}>:</Text>
                        <Text style={styles.value}>Rs. {formatINR(PurInvoiceDetails.SGSTAmt)}</Text>
                    </View>
                </>
            )}
            <View style={styles.totalRow}>
                <Text style={styles.label}><BoldLabel>Round Off</BoldLabel></Text>
                <Text style={styles.colon}>:</Text>
                <Text style={styles.value}>Rs. {formatINR(PurInvoiceDetails.RoundOff)}</Text>
            </View>
            <View style={styles.totalRow}>
                <Text style={styles.label}><BoldLabel>Total Amount</BoldLabel></Text>
                <Text style={styles.colon}>:</Text>
                <Text style={styles.value}>Rs. {formatINR(PurInvoiceDetails.BillAmt)}</Text>
            </View>
        </View>
    );

    const getTotalBillFooterHeight = () => {
        const fontSize = 14;
        const maxWidth = CONTENT_WIDTH * 0.35 - 10;
        const texts = [
            `Amount Before Tax: ₹${formatINR(PurInvoiceDetails.basicAmount || 0)}`,
            isIgst
                ? `IGST Amount: ₹${formatINR(PurInvoiceDetails.IGSTAmt || 0)}`
                : `CGST Amount: ₹${formatINR(PurInvoiceDetails.CGSTAmt || 0)}`,
            !isIgst && `SGST Amount: ₹${formatINR(PurInvoiceDetails.SGSTAmt || 0)}`,
            `Round Off: ₹${formatINR(PurInvoiceDetails.RoundOff || 0)}`,
            `Total Amount: ₹${formatINR(PurInvoiceDetails.BillAmt || 0)}`,
        ].filter(Boolean);
        const lines = texts.reduce((sum, text) => sum + estimateLines(text, maxWidth, fontSize), 0);
        const height = getTextHeight(fontSize, lines, 3, 3, 1, 0);
        console.log(`TotalBillFooter Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const AmountInWords = () => (
        <Text style={styles.amountInWords}><BoldLabel>RUPEES:</BoldLabel> {totalAmountInWords}</Text>
    );

    const getAmountInWordsHeight = () => {
        const fontSize = 14;
        const maxWidth = CONTENT_WIDTH - 10;
        const text = `RUPEES: ${totalAmountInWords}`;
        const lines = estimateLines(text, maxWidth, fontSize);
        const height = getTextHeight(fontSize, lines, 3, 3, 1, 0);
        console.log(`AmountInWords Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const PaymentTerms = () => (
        <Text style={styles.paymentTerms}>
            <BoldLabel>Payment Terms (Days) :</BoldLabel> 30{"\n"}
            <BoldLabel>Remark :</BoldLabel>
        </Text>
    );


    const getPaymentTermsHeight = () => {
        const fontSize = 14;
        const maxWidth = CONTENT_WIDTH - 10;
        const text = "Payment Terms (Days) : 30\nRemark :";
        const lines = getEstimatedLines(text, maxWidth, fontSize);
        const height = getTextHeight(fontSize, lines, 3, 3, 1, 0);
        console.log(`PaymentTerms Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const YoursFaithfully = () => (
        <Text style={styles.yoursFaithfully}>
            We hope you find the supplied items satisfactory. Thanking you for your business and assuring you of our best services at all times.{'\n'}Thanking You{'\n'}Yours Faithfully
        </Text>
    );

    const getYoursFaithfullyHeight = () => {
        const fontSize = 14;
        const maxWidth = CONTENT_WIDTH - 10;
        const text = "We hope you find the supplied items satisfactory. Thanking you for your business and assuring you of our best services at all times.\nThanking You\nYours Faithfully";
        const lines = getEstimatedLines(text, maxWidth, fontSize);
        const height = getTextHeight(fontSize, lines, 5, 5, 1, 0);
        console.log(`YoursFaithfully Height: ${height}, Lines: ${lines}`);
        return height;
    };

    const Terms = () => (
        <View style={styles.terms}>
            <View style={styles.termsLeft}>
                <Text>Terms & Conditions</Text>
                <Text># Subject To Ahmedabad Jurisdiction Only</Text>
                <Text># Goods once sold will not be taken back</Text>
                <Text># Payment Against delivery</Text>
                <Text># 28% interest will be applied if payment is not made within due date</Text>
                <Text># We are not responsible for any kind of damage or losses by transit</Text>
                <Text>Terms & Conditions</Text>
                <Text># Subject To Ahmedabad Jurisdiction Only</Text>
                <Text># Goods once sold will not be taken back</Text>
                <Text># Payment Against delivery</Text>
                <Text># 28% interest will be applied if payment is not made within due date</Text>
                <Text># We are not responsible for any kind of damage or losses by transit</Text>
            </View>
            <View style={styles.termsRight}>
                <Text>{company.CompanyName}</Text>
                <Text> </Text>
                <Text> </Text>
                <Text> </Text>
                <Text>(Authorised Signatory)</Text>
            </View>
        </View>
    );

    const getTermsHeight = () => {
        const leftFont = 10;
        const rightFont = 12;
        const leftMaxWidth = (CONTENT_WIDTH - 10) / 2;
        const rightMaxWidth = (CONTENT_WIDTH - 10) / 2;
        const leftText = `Terms & Conditions\n# Subject To Ahmedabad Jurisdiction Only\n# Goods once sold will not be taken back\n# Payment Against delivery\n# 28% interest will be applied if payment is not made within due date\n# We are not responsible for any kind of damage or losses by transit\nTerms & Conditions\n# Subject To Ahmedabad Jurisdiction Only\n# Goods once sold will not be taken back\n# Payment Against delivery\n# 28% interest will be applied if payment is not made within due date\n# We are not responsible for any kind of damage or losses by transit`;
        const rightText = `${company.CompanyName || ""}\n\n\n\n(Authorised Signatory)`;
        const leftLines = getEstimatedLines(leftText, leftMaxWidth, leftFont);
        const rightLines = getEstimatedLines(rightText, rightMaxWidth, rightFont);
        const leftHeight = getTextHeight(leftFont, leftLines, 5, 5, 0, 0);
        const rightHeight = getTextHeight(rightFont, rightLines, 5, 5, 0, 0);
        const height = Math.max(leftHeight, rightHeight) + 1 + 1;
        console.log(`Terms Height: ${height}, Left Lines: ${leftLines}, Right Lines: ${rightLines}`);
        return height;
    };

    const getHeaderHeight = () => {
        const height = getGSTDetailsHeaderHeight()
            + getTopSectionHeight()
            + getBottomSectionHeight()
            + getGreetingHeaderHeight();
        console.log(`Header Height: ${height}`);
        return height;
    };

    const getMainContentFixedHeight = () => {
        const height = 10;
        console.log(`MainContent Fixed Height: ${height}`);
        return height;
    };

    const getFooterHeight = (isLastPage) => {
        const height = isLastPage
            ? getTotalBillFooterHeight() + getAmountInWordsHeight() + getPaymentTermsHeight() + getYoursFaithfullyHeight() + getTermsHeight()
            : getTermsHeight();
        console.log(`${isLastPage ? "Last" : "Regular"} Footer Height: ${height}`);
        return height;
    };

    // === FIXED: Dynamic per-page available height ===
    const generatePages = () => {
        const companyWrapperHeight = getCompanyDetailsHeight();
        const perPageAvailable = PAGE_CONTENT_HEIGHT - companyWrapperHeight - PARENT_BORDER_TOP_BOTTOM; // Dynamic!

        const headerHeight = getHeaderHeight();
        const mainFixed = getMainContentFixedHeight();
        const tableHeadHeight = getTableHeadHeight();
        const tableFooterHeight = getTableFooterHeight();
        const regularFooterHeight = getFooterHeight(false);
        const lastFooterHeight = getFooterHeight(true);

        const totalRowHeight = getTableFooterHeight();
        const pages = [];
        let currentStart = 0;
        let pageNumber = 1;
        let sr = 1;

        while (currentStart < items.length) {
            let isLastPage = false;
            let footerHeight = regularFooterHeight;
            let availableForItems = perPageAvailable - headerHeight - mainFixed - footerHeight - tableHeadHeight - tableFooterHeight - FIXED_TABLE_FOOTER_SPACE - totalRowHeight;
            let currentItemsHeight = 0;
            let pageItems = [];
            const pageStartIndex = currentStart;

            while (currentStart < items.length) {
                const nextItem = items[currentStart];
                const nextRowHeight = getRowHeight(nextItem);
                const isLastItem = currentStart + 1 === items.length;
                const tempFooterHeight = isLastItem ? lastFooterHeight : regularFooterHeight;
                const tempAvailableForItems = perPageAvailable - headerHeight - mainFixed - tempFooterHeight - tableHeadHeight - tableFooterHeight - FIXED_TABLE_FOOTER_SPACE - totalRowHeight;

                if (currentItemsHeight + nextRowHeight > tempAvailableForItems) {
                    break;
                }

                pageItems.push(nextItem);
                currentItemsHeight += nextRowHeight;
                currentStart++;
            }

            if (currentStart >= items.length) {
                isLastPage = true;
                footerHeight = lastFooterHeight;
                let required = headerHeight + mainFixed + tableHeadHeight + currentItemsHeight + tableFooterHeight + footerHeight + FIXED_TABLE_FOOTER_SPACE + totalRowHeight;
                while (required > perPageAvailable && pageItems.length > 0) {
                    const lastItem = pageItems.pop();
                    currentStart--;
                    const lastRowHeight = getRowHeight(lastItem);
                    currentItemsHeight -= lastRowHeight;
                    required = headerHeight + mainFixed + tableHeadHeight + currentItemsHeight + tableFooterHeight + footerHeight + FIXED_TABLE_FOOTER_SPACE + totalRowHeight;
                }
            }

            if (pageItems.length === 0) {
                break;
            }

            console.log(`Page ${pageNumber} - Table total height: ${currentItemsHeight}px`);

            pages.push({
                items: pageItems,
                isLastPage,
                pageNumber,
                startSr: sr,
            });
            sr += pageItems.length;
            pageNumber++;
        }

        return pages;
    };

    const pages = generatePages();

    return (
        <Document>
            {pages.map((page) => (
                <Page size={{ width: 794, height: 1073 }} style={styles.page} key={page.pageNumber}>
                    <CompanyDetails />
                    <View style={styles.content}>
                        <GSTDetailsHeader />
                        <TopSection />
                        <BottomSection />
                        <GreetingHeader />
                        <MainContent items={page.items} startSr={page.startSr} />
                        <View style={styles.footer}>
                            {page.isLastPage && (
                                <>
                                    <TotalBillFooter />
                                    <AmountInWords />
                                    <PaymentTerms />
                                    <YoursFaithfully />
                                </>
                            )}
                            <Terms />
                        </View>
                    </View>
                    <Text
                        style={styles.pageNumber}
                        render={({ pageNumber }) => `Page No.: ${pageNumber}`}
                        fixed
                    />
                </Page>
            ))}
        </Document>
    );
};

export default InvoiceDocument;